import React, { useState } from "react";
import { History } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import { useGetCallFlowRevisionsQuery } from "@/store/rest_api";
import { CallFlowRevision } from "@/model/interface/CallFlow";
import { formatDateTime } from "@/util/date";

interface RevisionsSheetProps {
  flowId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onRestore: (revision: CallFlowRevision) => void;
  isRestoring: boolean;
}

export default function RevisionsSheet({ flowId, open, onOpenChange, onRestore, isRestoring }: RevisionsSheetProps) {
  const { data: revisions, isLoading } = useGetCallFlowRevisionsQuery(flowId, { skip: !open });
  const [revisionToRestore, setRevisionToRestore] = useState<CallFlowRevision | null>(null);

  const confirmRestore = () => {
    if (!revisionToRestore) {
      return;
    }
    onRestore(revisionToRestore);
    setRevisionToRestore(null);
  };

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent className="flex w-full flex-col sm:max-w-md">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2">
              <History className="h-4 w-4" />
              Version history
            </SheetTitle>
            <SheetDescription>Every save keeps a version.</SheetDescription>
          </SheetHeader>

          <div className="nowheel -mx-6 flex-1 overflow-y-auto px-6">
            {isLoading && (
              <div className="space-y-3 py-2">
                <Skeleton className="h-14 w-full" />
                <Skeleton className="h-14 w-full" />
                <Skeleton className="h-14 w-full" />
              </div>
            )}

            {!isLoading && (revisions?.length ?? 0) === 0 && (
              <p className="py-6 text-sm text-muted-foreground">No versions yet. The first save creates one.</p>
            )}

            {revisions?.map((revision, index) => (
              <div key={revision.id} className="flex items-start justify-between gap-3 border-b py-3 last:border-0">
                <div className="min-w-0 space-y-0.5">
                  <div className="text-sm font-semibold">{revision.summary || "Saved"}</div>
                  <div className="text-xs text-muted-foreground">
                    {formatDateTime(revision.createdAt)} by {revision.createdByName || "System"}
                    {revision.source === "RESTORE" && " (restored)"}
                    {" · "}
                    {revision.stepCount} {revision.stepCount === 1 ? "step" : "steps"}
                  </div>
                </div>
                {index === 0 ? (
                  <span className="status-pill-success shrink-0">Current</span>
                ) : (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="shrink-0"
                    disabled={isRestoring}
                    onClick={() => setRevisionToRestore(revision)}
                  >
                    Restore
                  </Button>
                )}
              </div>
            ))}
          </div>
        </SheetContent>
      </Sheet>

      <AlertDialog open={revisionToRestore !== null} onOpenChange={(isOpen) => !isOpen && setRevisionToRestore(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Restore this version?</AlertDialogTitle>
            <AlertDialogDescription>
              The flow goes back to the version saved on{" "}
              {revisionToRestore ? formatDateTime(revisionToRestore.createdAt) : ""}. If this flow is active,
              callers get it immediately. The version you have now stays in the history.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmRestore}>Restore</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
